/**
 * Andes Access Management Types
 * Types for permission requests and subscriptions
 * @module types/starnexus/andesAccess
 */

// ============================================================================
// Identity Types
// ============================================================================

/**
 * Types of identities that can request access to Andes tables
 */
export type IdentityType = 'DATANET_DBUSER' | 'IAM_ROLE' | 'TEAM' | 'CRADLE_ACCOUNT';

/**
 * Helper information for each identity type
 */
export interface IdentityTypeHelper {
  label: string;
  placeholder: string;
  validation: RegExp;
  example: string;
  description: string;
}

export const IDENTITY_TYPE_HELPERS: Record<IdentityType, IdentityTypeHelper> = {
  DATANET_DBUSER: {
    label: 'Datanet DB User',
    placeholder: 'Enter Datanet DB user name',
    validation: /^[a-z][a-z0-9_]*$/,
    example: 'delta_site_tier_2_prod',
    description: 'Datanet database user for SQL access'
  },
  IAM_ROLE: {
    label: 'IAM Role',
    placeholder: 'Enter IAM Role ARN',
    validation: /^arn:aws:iam::\d{12}:role\/.+$/,
    example: 'arn:aws:iam::123456789012:role/MyRole',
    description: 'AWS IAM Role ARN for programmatic access'
  },
  TEAM: {
    label: 'Abacus Team',
    placeholder: 'Enter Abacus Team ID',
    validation: /^amzn1\.abacus\.team\.[a-z0-9]+$/,
    example: 'amzn1.abacus.team.abc123xyz',
    description: 'Amazon Abacus team identifier'
  },
  CRADLE_ACCOUNT: {
    label: 'Cradle Account',
    placeholder: 'Enter Cradle account name',
    validation: /^[a-z][a-z0-9-]*$/,
    example: 'aurora-weaver-cradle-prod',
    description: 'Cradle account name for data pipeline access'
  }
};

// ============================================================================
// Permission Request Types
// ============================================================================

/**
 * Status of a permission request
 */
export type PermissionStatus = 'pending' | 'approved' | 'rejected';

/**
 * Business justification types (from Andes API)
 */
export type BusinessJustificationType =
  | 'Provide Amazon services for repeatable interactions'
  | 'Provide Amazons services for customers to view online'
  | 'Provide other Amazon services'
  | 'Improve Amazon Services - Non-Pseudonymized data'
  | 'Improve Amazon Services - Pseudonymized data'
  | 'Other';

/**
 * A permission request for accessing an Andes table
 */
export interface PermissionRequest {
  requestId: string;
  datasetId: string;
  datasetName: string;
  providerId: string;
  providerName: string;
  tableName: string;
  tableVersion: number;
  identityType: IdentityType;
  identityValue: string;
  businessJustificationType: BusinessJustificationType;
  businessJustification: string;
  status: PermissionStatus;
  requestedBy: string;
  requestedAt: Date;
  reviewedBy?: string;
  reviewedAt?: Date;
  reviewNotes?: string;
}

/**
 * Input for creating a permission request
 */
export interface CreatePermissionRequestInput {
  datasetIds: string[];
  identityType: IdentityType;
  identityValue: string;
  businessJustificationType: BusinessJustificationType;
  businessJustification: string;
}

/**
 * Parameters for querying permission requests
 */
export interface GetPermissionRequestsParams {
  status?: PermissionStatus | 'all';
  searchText?: string;
  datasetId?: string;
  identityType?: IdentityType;
}

// ============================================================================
// Subscription Types
// ============================================================================

/**
 * Types of subscription targets
 */
export type SubscriptionTargetType = 'DATASHARE' | 'CRADLE' | 'GLUE';

/**
 * Status of a subscription
 */
export type SubscriptionStatus = 'active' | 'pending' | 'failed' | 'deleted';

/**
 * Version migration strategy for subscriptions
 */
export type VersionMigrationStrategy = 'AUTO' | 'MANUAL';

/**
 * A configured subscription target
 */
export interface SubscriptionTarget {
  targetId: string;
  targetName: string;
  targetType: SubscriptionTargetType;
  description?: string;
  region?: string;
}

/**
 * A subscription linking an Andes table to a target
 */
export interface Subscription {
  subscriptionId: string;
  datasetId: string;
  datasetName: string;
  providerId: string;
  providerName: string;
  tableName: string;
  tableVersion: number;
  targetId: string;
  targetName: string;
  targetType: SubscriptionTargetType;
  region: string;
  versionMigrationStrategy: VersionMigrationStrategy;
  status: SubscriptionStatus;
  description?: string;
  createdBy: string;
  createdAt: Date;
  lastSyncAt?: Date;
  errorMessage?: string;
}

/**
 * Input for creating a subscription
 */
export interface CreateSubscriptionInput {
  datasetIds: string[];
  targetId: string;
  region: string;
  versionMigrationStrategy: VersionMigrationStrategy;
  description?: string;
}

/**
 * Parameters for querying subscriptions
 */
export interface GetSubscriptionsParams {
  targetType?: SubscriptionTargetType | 'all';
  status?: SubscriptionStatus | 'all';
  searchText?: string;
  datasetId?: string;
}

// ============================================================================
// UI Helper Types
// ============================================================================

/**
 * Badge color mapping for subscription target types
 */
export const TARGET_TYPE_BADGE_COLORS: Record<SubscriptionTargetType, 'blue' | 'green' | 'grey'> = {
  DATASHARE: 'blue',
  CRADLE: 'green',
  GLUE: 'grey'
};

/**
 * AWS regions available for subscriptions
 */
export const AWS_REGIONS = [
  { value: 'us-east-1', label: 'US East (N. Virginia)' },
  { value: 'us-west-2', label: 'US West (Oregon)' },
  { value: 'eu-west-1', label: 'Europe (Ireland)' },
  { value: 'ap-northeast-1', label: 'Asia Pacific (Tokyo)' },
  { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore)' }
] as const;

/**
 * Status indicator mapping for permission requests
 */
export const PERMISSION_STATUS_INDICATOR: Record<PermissionStatus, 'in-progress' | 'success' | 'error'> = {
  pending: 'in-progress',
  approved: 'success',
  rejected: 'error'
};

/**
 * Status indicator mapping for subscriptions
 */
export const SUBSCRIPTION_STATUS_INDICATOR: Record<SubscriptionStatus, 'in-progress' | 'success' | 'error' | 'stopped'> = {
  pending: 'in-progress',
  active: 'success',
  failed: 'error',
  deleted: 'stopped'
};
